import type { QueryClient } from "@tanstack/react-query";
import {
  Building2,
  CalendarClock,
  ClipboardCheck,
  Compass,
  FileClock,
  Handshake,
  LayoutDashboard,
  type LucideIcon,
  MapPin,
  Settings,
  UserCog,
  Users,
} from "lucide-react";
import type { ComponentType } from "react";
import type { RouteObject } from "react-router-dom";
import { queryKeys } from "../api/query-keys";
import {
  defaultAdminFilters,
  defaultPartnerFilters,
  defaultUserFilters,
  legalDocumentsQueryOptions,
  partnersQueryOptions,
  usersQueryOptions,
} from "../api/query-options";
import type { AdminPermission } from "../api/session-api";
import routeGovernance from "./admin-route-governance.json";
import {
  getAdminsPage,
  getPartnersPage,
  getTermsLegalVersionManagerPage,
  getUsersPage,
  loadAuditPage,
  loadLegalPages,
  loadPartnersPage,
  loadUsersPage,
} from "./route-modules";

type RouteSlice = "PILOT" | "PARTNER_EXTENSION";
type RouteKind = "surface" | "detail";

interface AdminRouteDefinition {
  mvpAvailability?:
    | "phase-01"
    | "phase-02"
    | "phase-03"
    | "phase-04"
    | "phase-05"
    | "phase-06"
    | "phase-07"
    | "calendar-dependency"
    | "deferred";
  alternativePermissions?: AdminPermission[];
  criticalCoverageProfiles: string[];
  kind: RouteKind;
  label: string;
  navigation?: boolean;
  path: string;
  permission: AdminPermission;
  slice: RouteSlice;
}

export interface AdminRouteHandle {
  alternativePermissions?: readonly AdminPermission[];
  criticalCoverageProfiles: readonly string[];
  label: string;
  navigation?: boolean;
  icon: LucideIcon;
  permission: AdminPermission;
  slice: RouteSlice;
  persistent?: boolean;
  getPage?: () => ComponentType | undefined;
  loadPage: () => Promise<{ default: ComponentType }>;
  warm?: (client: QueryClient) => { run: () => Promise<void>; cancel: () => Promise<void> };
}

export type AdminRoute = RouteObject & { path: string; handle: AdminRouteHandle };

type AdminRouteRuntime = Pick<AdminRouteHandle, "icon" | "loadPage"> &
  Partial<Pick<AdminRouteHandle, "getPage" | "persistent" | "warm">>;

const routeDefinitions = routeGovernance.routes as AdminRouteDefinition[];

/** Opt in delivered routes and required dependencies; future modules stay unmounted and cold. */
function isMvpAdminRoute(definition: AdminRouteDefinition) {
  return (
    definition.mvpAvailability === "phase-01" ||
    definition.mvpAvailability === "phase-02" ||
    definition.mvpAvailability === "phase-03" ||
    definition.mvpAvailability === "phase-04" ||
    definition.mvpAvailability === "phase-05" ||
    definition.mvpAvailability === "phase-06" ||
    definition.mvpAvailability === "phase-07" ||
    definition.mvpAvailability === "calendar-dependency"
  );
}

const routeRuntime: Record<string, AdminRouteRuntime> = {
  "/dashboard": {
    icon: LayoutDashboard,
    loadPage: () =>
      import("../features/dashboard/work-inbox-page").then((module) => ({
        default: module.WorkInboxPage,
      })),
  },
  "/audit": {
    icon: FileClock,
    loadPage: () => loadAuditPage().then((module) => ({ default: module.AuditPage })),
    warm: () => ({
      run: async () => undefined,
      cancel: async () => undefined,
    }),
  },
  "/audit/:interactionId": {
    icon: FileClock,
    loadPage: () =>
      import("../features/audit/audit-page").then((module) => ({
        default: module.AuditDetailPage,
      })),
  },
  "/legal": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.LegalHelpPage,
      })),
  },
  "/legal/terms": {
    icon: FileClock,
    persistent: true,
    getPage: getTermsLegalVersionManagerPage,
    loadPage: () =>
      loadLegalPages().then((module) => ({ default: module.TermsLegalVersionManagerPage })),
    warm: (client) => ({
      run: async () => {
        await client.fetchQuery(legalDocumentsQueryOptions());
      },
      cancel: () => client.cancelQueries({ queryKey: queryKeys.legal }),
    }),
  },
  "/legal/privacy": {
    icon: FileClock,
    loadPage: () =>
      import("../features/legal/legal-pages").then((module) => ({
        default: module.LegalVersionManagerPage,
      })),
  },
  "/public-content/help": {
    icon: FileClock,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.PublicContentVersionsPage,
      })),
  },
  "/public-content/faq": {
    icon: FileClock,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.PublicContentVersionsPage,
      })),
  },
  "/public-content/standards": {
    icon: FileClock,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.PublicContentVersionsPage,
      })),
  },
  "/legal/:kind": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/legal-pages").then((module) => ({
        default: module.LegalVersionManagerPage,
      })),
  },
  "/legal/drafts/new": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/legal-editor-page").then((module) => ({
        default: module.LegalEditorPage,
      })),
  },
  "/legal/drafts/:documentId/edit": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/legal-editor-page").then((module) => ({
        default: module.LegalEditorPage,
      })),
  },
  "/public-content/:kind": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.PublicContentVersionsPage,
      })),
  },
  "/public-content/versions/:documentId": {
    icon: Settings,
    loadPage: () =>
      import("../features/legal/public-content-pages").then((module) => ({
        default: module.PublicContentDetailPage,
      })),
  },

  "/admins": {
    icon: UserCog,
    persistent: true,
    getPage: getAdminsPage,
    loadPage: () => loadUsersPage().then((module) => ({ default: module.AdminsPage })),
    warm: (client) => ({
      run: async () => {
        await client.fetchQuery(usersQueryOptions(defaultAdminFilters));
      },
      cancel: () => client.cancelQueries({ queryKey: queryKeys.users(defaultAdminFilters) }),
    }),
  },
  "/profile-reviews": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.ProfileReviewsPage,
      })),
  },
  "/email-changes": {
    icon: Users,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.EmailChangesPage,
      })),
  },
  "/email-changes/:changeId": {
    icon: Users,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.EmailChangeDetailPage,
      })),
  },
  "/profile-reviews/:reviewId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.ProfileReviewDetailPage,
      })),
  },
  "/itinerary-reviews": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.ItineraryReviewsPage,
      })),
  },
  "/itinerary-reviews/:reviewId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.ItineraryReviewDetailPage,
      })),
  },
  "/video-verifications": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.VideoVerificationsPage,
      })),
  },
  "/video-verifications/:submissionId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.VideoVerificationDetailPage,
      })),
  },
  "/urgent-notices": {
    icon: MapPin,
    loadPage: () =>
      import("../features/urgent-notices/urgent-notice-pages").then((module) => ({
        default: module.UrgentNoticesPage,
      })),
  },
  "/urgent-notices/:noticeId": {
    icon: MapPin,
    loadPage: () =>
      import("../features/urgent-notices/urgent-notice-pages").then((module) => ({
        default: module.UrgentNoticeDetailPage,
      })),
  },
  "/city-reports": {
    icon: MapPin,
    loadPage: () =>
      import("../features/city-reports/city-report-pages").then((module) => ({
        default: module.CityReportsPage,
      })),
  },
  "/city-reports/:reportId": {
    icon: MapPin,
    loadPage: () =>
      import("../features/city-reports/city-report-pages").then((module) => ({
        default: module.CityReportDetailPage,
      })),
  },
  "/support-requests": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/support/support-pages").then((module) => ({
        default: module.SupportRequestsPage,
      })),
  },
  "/support-requests/:requestId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/support/support-pages").then((module) => ({
        default: module.SupportRequestDetailPage,
      })),
  },
  "/cyra-suggestions": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/suggestions/suggestion-pages").then((module) => ({
        default: module.SuggestionsPage,
      })),
  },
  "/cyra-suggestions/:suggestionId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/suggestions/suggestion-pages").then((module) => ({
        default: module.SuggestionDetailPage,
      })),
  },
  "/review-sessions": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/reviews/review-session-pages").then((module) => ({
        default: module.ReviewSessionsPage,
      })),
  },
  "/review-sessions/:sessionId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/reviews/review-session-pages").then((module) => ({
        default: module.ReviewSessionDetailPage,
      })),
  },
  "/social-groups": {
    icon: Users,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialGroupsPage,
      })),
  },
  "/social-groups/:groupId": {
    icon: Users,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialGroupDetailPage,
      })),
  },
  "/social-reviews": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialReviewsPage,
      })),
  },
  "/social-reviews/:reviewId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialReviewDetailPage,
      })),
  },
  "/social-message-reviews": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialMessageReviewsPage,
      })),
  },
  "/social-message-reviews/:messageId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialMessageReviewDetailPage,
      })),
  },
  "/social-reports": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialReportsPage,
      })),
  },
  "/social-reports/:reportId": {
    icon: ClipboardCheck,
    loadPage: () =>
      import("../features/social/social-pages").then((module) => ({
        default: module.SocialReportDetailPage,
      })),
  },
  "/account-deletions": {
    icon: Users,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.AccountDeletionsPage,
      })),
  },
  "/account-deletions/:deletionId": {
    icon: Users,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.AccountDeletionDetailPage,
      })),
  },
  "/administrators/:invitationId": {
    icon: UserCog,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.MvpInvitationDetailPage,
      })),
  },
  "/schedules": {
    icon: CalendarClock,
    loadPage: () =>
      import("../features/partners/schedules-page").then((module) => ({
        default: module.SchedulesPage,
      })),
  },
  "/partners": {
    icon: Handshake,
    persistent: true,
    getPage: getPartnersPage,
    loadPage: () => loadPartnersPage().then((module) => ({ default: module.PartnersPage })),
    warm: (client) => ({
      run: async () => {
        await client.fetchQuery(partnersQueryOptions(defaultPartnerFilters));
      },
      cancel: () => client.cancelQueries({ queryKey: queryKeys.partners(defaultPartnerFilters) }),
    }),
  },
  "/experiences": {
    icon: Compass,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.ExperiencesPage,
      })),
  },
  "/destinations": {
    icon: MapPin,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.DestinationsPage,
      })),
  },
  "/cities": {
    icon: Building2,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.CitiesPage,
      })),
  },
  "/users": {
    icon: Users,
    persistent: true,
    getPage: getUsersPage,
    loadPage: () => loadUsersPage().then((module) => ({ default: module.UsersPage })),
    warm: (client) => ({
      run: async () => {
        await client.fetchQuery(usersQueryOptions(defaultUserFilters));
      },
      cancel: () => client.cancelQueries({ queryKey: queryKeys.users(defaultUserFilters) }),
    }),
  },
  "/administrators": {
    icon: UserCog,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.MvpInvitationsPage,
      })),
  },
  "/cities/:cityId": {
    icon: Building2,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.CityDetailPage,
      })),
  },
  "/destinations/:destinationId": {
    icon: MapPin,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.DestinationDetailPage,
      })),
  },
  "/experiences/:experienceId": {
    icon: Compass,
    loadPage: () =>
      import("../features/catalogue/catalogue-pages").then((module) => ({
        default: module.ExperienceDetailPage,
      })),
  },
  "/admins/:userId": {
    icon: UserCog,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.MvpUserDetailPage,
      })),
  },
  "/users/:userId": {
    icon: Users,
    loadPage: () =>
      import("../features/accounts/account-pages").then((module) => ({
        default: module.MvpUserDetailPage,
      })),
  },
  "/partners/:partnerId": {
    icon: Handshake,
    loadPage: () =>
      import("../features/partners/partners-page").then((module) => ({
        default: module.PartnerDetailPage,
      })),
  },
  "/partner-offerings/:offeringId": {
    icon: Compass,
    loadPage: () =>
      import("../features/partners/partner-child-detail").then((module) => ({
        default: module.PartnerOfferingDetailPage,
      })),
  },
  "/partner-schedules/:scheduleId": {
    icon: FileClock,
    loadPage: () =>
      import("../features/partners/partner-child-detail").then((module) => ({
        default: module.PartnerScheduleDetailPage,
      })),
  },
};

const configuredPaths = new Set(routeDefinitions.map((definition) => definition.path));
for (const path of Object.keys(routeRuntime))
  if (!configuredPaths.has(path)) throw new Error(`Admin route runtime is not governed: ${path}`);

function createRoute(definition: AdminRouteDefinition): AdminRoute {
  const runtime = routeRuntime[definition.path];
  if (!runtime) throw new Error(`Admin route has no runtime: ${definition.path}`);
  return {
    path: definition.path,
    handle: {
      criticalCoverageProfiles: [...definition.criticalCoverageProfiles],
      label: definition.label,
      ...(definition.navigation === undefined ? {} : { navigation: definition.navigation }),
      permission: definition.permission,
      ...(definition.alternativePermissions
        ? { alternativePermissions: definition.alternativePermissions }
        : {}),
      slice: definition.slice,
      ...runtime,
    },
  };
}

export const adminSurfaceRoutes = routeDefinitions
  .filter((definition) => definition.kind === "surface" && isMvpAdminRoute(definition))
  .map(createRoute);

export const adminDetailRoutes = routeDefinitions
  .filter((definition) => definition.kind === "detail" && isMvpAdminRoute(definition))
  .map(createRoute);

export const adminRoutes = [...adminSurfaceRoutes, ...adminDetailRoutes];

function matchesRoute(pattern: string, pathname: string) {
  const candidate = pathname.split(/[?#]/u, 1)[0] ?? pathname;
  const patternParts = pattern.split("/").filter(Boolean);
  const candidateParts = candidate.split("/").filter(Boolean);
  return (
    patternParts.length === candidateParts.length &&
    patternParts.every((part, index) => part.startsWith(":") || part === candidateParts[index])
  );
}

export function adminRouteLabel(pathname: string) {
  const exact = adminRoutes.find((route) => matchesRoute(route.path, pathname));
  if (exact) return exact.handle.label;
  return (
    adminSurfaceRoutes.find(
      (route) => pathname === route.path || pathname.startsWith(`${route.path}/`),
    )?.handle.label ?? "Cyra"
  );
}
